import { orderBy } from "lodash";
import PropTypes from "prop-types";
import React, { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { useAddQuery, useForm, useFilter, getUrlData } from "../../../../hooks";
import { getExchangeTransactionAction } from "../../../../store/actions/partnerAnalytics";
import analyticsConstant from "../../../../util/analyticsConstant";
import constant from "../../../../util/constant";
import { useSetUrlParams } from "../../../../util/helpers";
import OfferRewardTxnFilter from "./offerRewardTxnFilter";
import OfferRewardTxnList from "./offerRewardTxnList";

const { API_STATUS } = constant;
const { ANALYTICS_TAB } = analyticsConstant;

const OfferRewardTransactions = ( {
  searchKey = "",
  setPreviousData,
  showFilter,
  gameId = "",
  gameName = "",
  tab = ANALYTICS_TAB.OFFER_REWARD_TXN,
} ) => {
  const isGameId = Boolean( gameId );
  const ref = useRef( null );
  const dispatch = useDispatch();
  const location = useLocation();
  const { addQuery } = useAddQuery();
  const { setPureData, filteredData, filterData } = useFilter();

  const {
    exchangeTransaction,
    apiType,
    status,
    usdPerQxPoint,
  } = useSelector( state => ( {
    exchangeTransaction: state.partnerAnalytics.exchangeTransaction,
    apiType: state.partnerAnalytics.apiType,
    status: state.partnerAnalytics.status,
    usdPerQxPoint: state.partner.partnerDetail.configuration?.usdPerQxPoint || 0,
  } ) );

  const { transaction, gameNames, offerNames, gamerNames, qxPointsRange, gamerPlaceRange } = exchangeTransaction;

  const { values, handleChange } = useForm(
    {
      searchKey: "",
      sortBy: "",
      sortOrder: "",
      gamerName: [],
      gameIds: [],
      offerNames: [],
      minQxPoint: 0,
      maxQxPoint: 0,
      startDate: null,
      endDate: null,
      searchCol: [ "name", "gamerName", "offerName" ],
      compareKeyArray: [ "QXPoints", "gamerName", "name", "myPlace" ],
      minGamerPlace: 0,
      maxGamerPlace: 0,
    },
    {}
  );

  const requestParams = Object.assign(
    {
      isSingleDate: true,
      searchCol: values.searchCol,
      sortOrder: values.sortOrder,
      sortBy: values.sortBy,
      searchKey: values.searchKey,
      gamerName: values.gamerName?.map( value => value.value ) || [],
      gameIds: values.gameIds?.map( value => value.value ) || [],
      offerNames: values.offerNames?.map( value => value.value ) || [],
      minQxPoint: values.minQxPoint,
      maxQxPoint: values.maxQxPoint,
      startDate: values.startDate,
      endDate: values.endDate,
      compareKeyArray: values.compareKeyArray,
      minGamerPlace: values.minGamerPlace,
      maxGamerPlace: values.maxGamerPlace,
    }
  );

  const handleSorting = ( sortBy, orderType ) => {
    handleChange( "sortBy", sortBy );
    handleChange( "sortOrder", orderType );
    addQuery( useSetUrlParams( "sortBy", sortBy ), location.state, isGameId );
    addQuery( useSetUrlParams( "sortOrder", orderType ), location.state, isGameId );
  };

  useEffect( () => {
    dispatch( getExchangeTransactionAction(
      { type: ( tab === ANALYTICS_TAB.OFFER_REWARD_TXN ) ? ANALYTICS_TAB.OFFER_REWARD_TXN : ANALYTICS_TAB.TOURNAMENT_REWARD_TXN }
    ) );
  }, [] );

  useEffect( () => {
    if( gameId && gameName ) {
      const selectedGame = { label: gameName, value: gameName };

      handleChange( "gameIds", [ selectedGame ] );
      addQuery( useSetUrlParams( "gameIds", JSON.stringify( [ selectedGame ] ) ), location.state, isGameId );
    }
  }, [ gameId, gameName ] );

  useEffect( () => {
    if(
      ( status === API_STATUS.SUCCESS )
    ) {
      const sortTransactionList = orderBy( transaction, [ "date" ], [ "desc" ] );

      handleChange( "minQxPoint", qxPointsRange.min );
      handleChange( "maxQxPoint", qxPointsRange.max );
      handleChange( "minGamerPlace", gamerPlaceRange.min );
      handleChange( "maxGamerPlace", gamerPlaceRange.max );
      setPureData( sortTransactionList );
      getUrlData( handleChange );
      setPreviousData();
    }
  }, [ status, apiType ] );

  useEffect( () => {
    filterData( requestParams );
  }, [
    values.sortOrder,
    values.sortBy,
    values.searchKey,
    values.gamerName,
    values.gameIds,
    values.offerNames,
    values.minQxPoint,
    values.maxQxPoint,
    values.startDate,
    values.endDate,
    values.minGamerPlace,
    values.maxGamerPlace,
  ] );

  useEffect( () => {
    handleChange( "searchKey", searchKey );
  }, [ searchKey ] );

  return ( <>
    <div className={`filter-tabs-wrapper mb-0 ${ ( !showFilter ) ? "" : "remove-filter" }`}>
      <OfferRewardTxnFilter
        values={values}
        handleChange={handleChange}
        addQuery={addQuery}
        gamerNames={gamerNames}
        gameNames={gameNames}
        offerNames={offerNames}
        transactionRanges={qxPointsRange}
        isGameId={isGameId}
        locationState={location.state}
        gamerPlaceRange={gamerPlaceRange}
        tab={tab}
        ref={ref}
      />
    </div>
    <div className={`table-container ${ ( tab === ANALYTICS_TAB.OFFER_REWARD_TXN )
      ? "partner-analytics-transaction"
      : "partner-analytics-tournament-reward-transaction" }`}>
      <OfferRewardTxnList
        showFilter={showFilter}
        handleSorting={handleSorting}
        dataList={filteredData}
        tab={tab}
        usdPerQxPoint={usdPerQxPoint}
      />
    </div>
  </>
  );
};

OfferRewardTransactions.propTypes = {
  searchKey: PropTypes.string,
  gameId: PropTypes.string,
  setPreviousData: PropTypes.func,
  gameName: PropTypes.string,
  showFilter: PropTypes.string,
  tab: PropTypes.string,
};

export default OfferRewardTransactions;
